import once from 'lodash/once';

const config = {
  attributes: true,
  childList: true,
};
/**
 * Generic number input component that stores internally a float value of an integer,
 * but externally displays a formatted string per locale settings. Binds into a stage
 * component, works autonomously and invisbly behind the scenes. Only downside is that
 * input.value = '123' doesn't trigger render. Instead input.write(123) needs to be called.
 * Only workaround would be to use polling, because directly writing to value doesn't trigger
 * any event that is observable.
 *
 * TO-DO: Test browser compatibility. Only Mac Chrome tested.
 *
 * Please avoid jQuery here if possible. Vanilla JS does wonders.
 *
 * @type {Object}
 * @author KL
 */
const dynamicNumber = {
  /**
   * Init engine. Wrapped in lodash once to make sure it's not called repeatedly
   */
  init: once(function() {
    this.observer = new MutationObserver(this.observerCallback);

    this.newNodeObserver = new MutationObserver(this.newNodeObserverCallback);
    document.querySelectorAll('.wuwei').forEach(container => {
      this.newNodeObserver.observe(container, { childList: true, subtree: true });
    });

    // Find all the span elements from a page and bind the additional functionality to those
    document.querySelectorAll('.dynamic-number').forEach(this.initSpan.bind(this));
  }),

  observer: null,

  initSpan: function(span) {
    span.precision = parseInt(span.getAttribute('data-precision')) || 0;
    span.hideZero = parseInt(span.getAttribute('data-hide-zero')) || 0;

    span.formatter = new Intl.NumberFormat('fi-FI', {
      style: 'decimal',
      minimumFractionDigits: span.precision,
      maximumFractionDigits: span.precision,
    });
    span.read = this.read;
    span.write = this.write;
    span.render = this.render;
    span.round = this.round;

    span.display = document.getElementById(span.getAttribute('id') + '-display');

    span.write(span.getAttribute('data-value'));
    span.render(span.read());

    this.observer.observe(span, config);
  },

  /**
   * Callback handler for mutationObserver
   * @param  {object} mutationRecord https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   * @param  {object} observer       https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   */
  observerCallback: function(mutationRecord, observer) {
    for (const event of mutationRecord) {
      if (event.attributeName === 'data-value') {
        event.target.render(event.target.read());
      }
    }
  },

  /**
   * Callback handler for mutationObserver
   * @param  {object} mutationRecord https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   * @param  {object} observer       https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   */
  newNodeObserverCallback: function(mutationRecord, observer) {
    for (const event of mutationRecord) {
      if (event.type === 'childList' && event.addedNodes.length) {
        for (const addedNode of event.addedNodes) {
          if (typeof addedNode.querySelectorAll !== 'undefined') {
            addedNode.querySelectorAll('.dynamic-number').forEach(dynamicNumber.initSpan.bind(dynamicNumber));
          }
        }
      }
    }
  },

  /**
   * Returns the internal value
   * @return {float} internal value
   */
  read: function() {
    let value = parseFloat(this.getAttribute('data-value'));
    value = !isNaN(value) ? value : 0;
    return value;
  },

  /**
   * Validates and updates given input to internal value
   * @param  {float} value input
   * @return {float}       parsed internal value
   */
  write: function(value) {
    value = parseFloat(value);
    value = !isNaN(value) ? this.round(value) : 0;
    this.setAttribute('data-value', value);
    this.value = value;
    return value;
  },

  /**
   * Renders the external value display
   * @param  {float} value numeric value
   */
  render: function(value) {
    this.display.innerHTML = this.formatter.format(value);
    this.setAttribute('data-show', this.hideZero && value !== 0);
  },

  /**
   * [round description]
   * @param  {float} value numbervalue
   * @return {float} rounded to precision
   */
  round: function(value) {
    return Math.round(value * Math.pow(10, this.precision)) / Math.pow(10, this.precision);
  },
};

export default dynamicNumber;
